import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `/**
 * How deeply is \`term\` nested inside \`query\`?
 *
 * @returns the number of enclosing parentheses, or -1 if the term is not there
 */
function termLevel(query: string, term: string): number {
  // TODO
  return -1;
}
`;

const solution = `const OPERATORS = new Set(["AND", "OR", "NOT"]);
const BREAKS = new Set([" ", "\\t", "\\n", "(", ")", '"']);

function termLevel(query: string, term: string): number {
  let depth = 0;
  let best = -1;
  let i = 0;

  // One pass, tokenising as we go. The temptation is query.indexOf(term) plus a
  // count of the parentheses before it, and that is wrong twice over: it matches
  // inside longer words, and it cannot tell an operator from a term.
  while (i < query.length) {
    const ch = query[i]!;

    if (ch === "(") { depth++; i++; continue; }
    if (ch === ")") {
      depth--;
      // Closing more than was opened means the rest of the query is nonsense.
      if (depth < 0) throw new Error("unbalanced parentheses");
      i++;
      continue;
    }
    if (ch === " " || ch === "\\t" || ch === "\\n") { i++; continue; }

    let token: string;
    let quoted = false;

    if (ch === '"') {
      const end = query.indexOf('"', i + 1);
      if (end === -1) throw new Error("unterminated quoted phrase");
      token = query.slice(i + 1, end);
      quoted = true;
      i = end + 1;
    } else {
      let j = i;
      while (j < query.length && !BREAKS.has(query[j]!)) j++;
      token = query.slice(i, j);
      i = j;
    }

    // A bare AND is an operator. A quoted "AND" is somebody searching for the
    // word, which is the whole reason quoting exists.
    if (!quoted && OPERATORS.has(token)) continue;

    if (token === term && (best === -1 || depth < best)) best = depth;
  }

  if (depth !== 0) throw new Error("unbalanced parentheses");
  return best;
}`;

const harness = HARNESS_KIT + `
if (typeof termLevel !== "function") {
  throw new Error("termLevel is not defined. Keep the function name as given.");
}

function __level(query, term) {
  return function () { return termLevel(query, term); };
}

const __tests = [
  {
    name: "reports the nesting depth of each term",
    about: "The basic contract: top level is 0, one set of brackets is 1.",
    run: function () {
      __expectExact('termLevel("A AND (B OR C)", "A")', __level("A AND (B OR C)", "A"), 0);
      __expectExact('termLevel("A AND (B OR C)", "B")', __level("A AND (B OR C)", "B"), 1);
      __expectExact('termLevel("A AND (B OR C)", "C")', __level("A AND (B OR C)", "C"), 1);
    }
  },
  {
    name: "a term that is not in the query is -1",
    about: "Absent is a distinct answer from 'at the top level'. Returning 0 for both makes the caller think it found something.",
    run: function () {
      __expectExact('termLevel("A AND (B OR C)", "D")', __level("A AND (B OR C)", "D"), -1);
    }
  },
  {
    name: "counts several levels of nesting",
    about: "Depth is cumulative, so it has to be tracked as a running count rather than looked for once.",
    run: function () {
      var q = "(revenue OR (margin AND (ebitda OR ebit)))";
      __expectExact('termLevel(q, "revenue")', __level(q, "revenue"), 1);
      __expectExact('termLevel(q, "margin")', __level(q, "margin"), 2);
      __expectExact('termLevel(q, "ebitda")', __level(q, "ebitda"), 3);
    }
  },
  {
    name: "operators are not terms",
    hidden: true,
    about: "AND, OR and NOT are syntax. Reporting a level for them means the tokeniser never separated grammar from content.",
    run: function () {
      __expectExact('termLevel("A AND (B OR NOT C)", "AND")', __level("A AND (B OR NOT C)", "AND"), -1);
      __expectExact('termLevel("A AND (B OR NOT C)", "OR")', __level("A AND (B OR NOT C)", "OR"), -1);
      __expectExact('termLevel("A AND (B OR NOT C)", "NOT")', __level("A AND (B OR NOT C)", "NOT"), -1);
    }
  },
  {
    name: "lower-case operator words are ordinary terms",
    hidden: true,
    about: "Operators are upper case. 'and' in a phrase is a word somebody is searching for, and swallowing it silently loses part of the query.",
    run: function () {
      __expectExact('termLevel("profit and loss", "and")', __level("profit and loss", "and"), 0);
    }
  },
  {
    name: "matches whole tokens, never substrings",
    hidden: true,
    about: "indexOf finds 'brand' inside 'branding' and reports a level for a term that is not in the query. This is the single most common way to get this wrong.",
    run: function () {
      __expectExact('termLevel("branding", "brand")', __level("branding", "brand"), -1);
      __expectExact('termLevel("branding AND (brand)", "brand")', __level("branding AND (brand)", "brand"), 1);
    }
  },
  {
    name: "a repeated term reports its shallowest level",
    hidden: true,
    about: "The same term can appear in several branches. The shallowest is the one that says how broadly the query is constrained, and it must not depend on scan order.",
    run: function () {
      var q = "((deep OR x) AND deep)";
      __expectExact('termLevel(q, "deep")', __level(q, "deep"), 1);
      var q2 = "(shallow AND (a OR (shallow)))";
      __expectExact('termLevel(q2, "shallow")', __level(q2, "shallow"), 1);
    }
  },
  {
    name: "a quoted phrase is one term, spaces and all",
    hidden: true,
    about: "Splitting on whitespace turns a quoted phrase into several terms, and the phrase itself then matches nothing.",
    run: function () {
      var q = 'ai AND ("machine learning" OR nlp)';
      __expectExact('termLevel(q, "machine learning")', __level(q, "machine learning"), 1);
      __expectExact('termLevel(q, "machine")', __level(q, "machine"), -1);
    }
  },
  {
    name: "a quoted operator is a term, not syntax",
    hidden: true,
    about: "Quoting exists precisely so you can search for a word that collides with the grammar. Filtering by token text alone breaks it.",
    run: function () {
      __expectExact('termLevel(\\'x OR "AND"\\', "AND")', __level('x OR "AND"', "AND"), 0);
    }
  },
  {
    name: "brackets need no whitespace around them",
    hidden: true,
    about: "Queries are typed by hand, so (a OR b)AND c arrives. A tokeniser that splits on spaces reads 'b)AND' as one token.",
    run: function () {
      __expectExact('termLevel("(a OR b)AND c", "b")', __level("(a OR b)AND c", "b"), 1);
      __expectExact('termLevel("(a OR b)AND c", "c")', __level("(a OR b)AND c", "c"), 0);
      __expectExact('termLevel("((a))", "a")', __level("((a))", "a"), 2);
    }
  },
  {
    name: "unbalanced brackets throw",
    hidden: true,
    about: "A malformed query has no honest answer. Returning a number for it means a typo silently changes what the user searched for.",
    run: function () {
      __expectThrows('termLevel("(a AND b", "a")', __level("(a AND b", "a"), "the bracket is never closed");
      __expectThrows('termLevel("a) AND b", "a")', __level("a) AND b", "a"), "it closes a bracket that was never opened");
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "query.indexOf(term) does find your term. Ask what it does when the term is brand and the query says branding. You are not searching a string, you are reading it one character at a time and counting brackets as you pass them.",
    code: `function termLevel(query: string, term: string): number {
  let depth = 0;   // brackets currently open
  let best = -1;   // shallowest depth this term was seen at
  let i = 0;

  while (i < query.length) {
    const ch = query[i]!;
    if (ch === "(") { depth++; i++; continue; }
    if (ch === ")") { depth--; i++; continue; }
    if (ch === " ") { i++; continue; }

    // ... everything else is the start of a token
  }

  return best;
}`,
  },
  {
    label: "Approach",
    body: "Read a whole token before judging it. A quote opens a token that runs to the next quote, spaces and all; anything else runs until a character that breaks a token. Only then compare it to the term, at whatever depth the counter says now.",
    code: `const OPERATORS = new Set(["AND", "OR", "NOT"]);
const BREAKS = new Set([" ", "\\t", "\\n", "(", ")", '"']);

let token: string;
let quoted = false;

if (ch === '"') {
  const end = query.indexOf('"', i + 1);
  token = query.slice(i + 1, end);   // spaces inside a phrase are part of it
  quoted = true;
  i = end + 1;
} else {
  let j = i;
  while (j < query.length && !BREAKS.has(query[j]!)) j++;
  token = query.slice(i, j);         // whole token, so "brand" never matches "branding"
  i = j;
}

if (!quoted && OPERATORS.has(token)) continue;
if (token === term && (best === -1 || depth < best)) best = depth;`,
  },
  {
    label: "The trap",
    body: "Once quoting exists a token can contain a space, so splitting on whitespace is off the table. A bare AND is syntax while a quoted \"AND\" is a term, so you cannot decide by the token's text alone: you have to remember how it was read. And a bracket that never closes, or one that closes too many, has no honest answer, so it throws.",
  },
];

export const booleanQueryDepth: Drill = {
  id: "boolean-query-depth",
  track: "typescript",
  title: "Nesting depth in a boolean query",
  pattern: "expression-parsing",
  difficulty: "hard",
  basePoints: 300,
  timeBudgetMs: 30 * 60 * 1000,
  statement: `Users type boolean queries into a search box. You need to know how deeply a given term sits inside one, because a term at the top level constrains every result and a term four brackets down barely constrains anything.

\`\`\`ts
function termLevel(query: string, term: string): number;
\`\`\`

Return the number of enclosing brackets around the term, or \`-1\` if it is not in the query.

## Requirements

- **Depth is the bracket count.** Not inside any bracket is level 0.
- **\`AND\`, \`OR\` and \`NOT\` in upper case are operators**, not terms. Lower case, they are ordinary words.
- **Terms are whole tokens.** \`brand\` does not match inside \`branding\`.
- **A quoted phrase is a single term**, spaces included, and quoting an operator makes it a term again.
- **A repeated term reports its shallowest level.**
- **Unbalanced brackets throw.**

## Example

\`\`\`ts
termLevel("A AND (B OR C)", "A");   // 0
termLevel("A AND (B OR C)", "B");   // 1
termLevel("A AND (B OR C)", "D");   // -1

termLevel('ai AND ("machine learning" OR nlp)', "machine learning");   // 1
\`\`\`

## Where this gets interesting

Almost everyone reaches for \`query.indexOf(term)\` and then counts the brackets before that offset. It is fast to write and wrong in two directions at once: it matches inside longer words, and it cannot tell the operator \`AND\` from a term somebody quoted. Both failures are silent, and both change what the user believes they searched for.

The shape that survives is a single left-to-right pass that tokenises as it goes, carrying the depth as a counter. Quoting is the part worth thinking about early rather than bolting on: the moment a token can contain a space, splitting on whitespace stops being an option at all.`,
  hints,
  starter,
  harness,
  solution,
  complexity: "One pass over the query, O(n) time and O(1) extra space beyond the token being read.",
};
